import { getConfig } from "@/src/config";
import { createContext, useRef } from "react";
import { FilterParams, paramsToQuery, SearchParams } from "./search_params.model";
import { boatsFetch } from "@/src/lib/boatsFetch";
import { cached } from "@/src/components/ui/cached";
import { useState, useCallback } from "react";

export type SearchParamsPeriod = {
  dateFrom: string;
  dateTo: string;
};

export interface YachtData {
  id: string;
  slug: string;
  name: string;
  model: string;
  type: string;
  pictures: string[];
  features: string;
  amenities: string[];
  cabins: number;
  berths: number;
  bathrooms: number;
  sail: string;
  location: string;
  operator: string;
  operatorRating: number;
  year: number;
  brands: string[];
  listPrice: number;
  price: number;
  deposit: number;
  startLocId: string;
  endLocId: string;
  startTime: string; // ISO date string
  endTime: string; // ISO date string
  status: string;
}

export type SearchResults = {
  data: YachtData[];
  total_results: number;
  total_pages: number;
  page_size: number;
  page: number;
};

export const getApiFilters = cached(() => boatsFetch<FilterParams>(`/filters?target=ha`));

export function useSearch() {
  const [isSearching, setIsSearching] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const performSearch = useCallback(async (params: SearchParams) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setIsSearching(true);
    try {
      const searchUrl = getConfig().SEARCH_URL;
      const filterParams = await getApiFilters();

      const url = `${searchUrl}/search?${paramsToQuery(filterParams, params)}`;

      const response = await fetch(url, {
        mode: "cors",
        signal: abortControllerRef.current.signal,
      });

      const results: SearchResults = await response.json();
      return results;
    } catch (err: any) {
      if (err.name === "AbortError") {
        // expected
        return {} as never;
      }
      throw err;
    } finally {
      setIsSearching(false);
    }
  }, []);

  return { search: performSearch, isSearching };
}

export const SearchParamsContext = createContext<SearchParams>({} as any);
